import { useMemo } from 'react';
import { useSaSorgu } from './useSatisVerisi';
import { SectionCard, fmtTRY } from '../yeni-design/primitives';
import type { Donem } from './donem';
import type { SaKaynak } from './ekranKaynaklari';
import { kanalRengi, kanalSirala } from './kanalRenk';
import { KANAL_AGACI, kanalGrubu, sepetHesaplanirMi } from './kanal';
import { kanalSuz, subeSuz } from './subeSuzgeci';
import { BlokIskeleti } from './BlokIskeleti';
import { sayi, type KanalSatiri } from './ozetHesaplari';

export interface SubeHucre {
  ciro: number;
  fis: number;
}

export interface SubeSatiri {
  sube: string;
  hucreler: Record<string, SubeHucre>;
  toplam: number;
  /** Mağaza kanalinin sepeti (spec §7.2); Mağaza fisi yoksa null. */
  sepet: number | null;
}

export interface SubeTablosu {
  sutunlar: string[];
  satirlar: SubeSatiri[];
  toplam: SubeSatiri;
}

/**
 * 990040 satirlari (sube/kanal suzgeci uygulanmis) -> sube × kanal grubu tablosu. Satir toplami subenin TUM
 * gruplarinin cirosu; `sutunlar` veride gecen her grubu kapsamali (cagiran kurar), yoksa Toplam sutunlarin
 * toplamindan buyuk gorunur. Siralama toplam ciroya gore azalan.
 */
export function subeTablosu(rows: KanalSatiri[], sutunlar: string[]): SubeTablosu {
  const map = new Map<string, Record<string, SubeHucre>>();
  for (const r of rows) {
    const sube = String(r.lokasyon);
    const grup = String(r.kanal_grup);
    const h = map.get(sube) ?? {};
    const c = h[grup] ?? { ciro: 0, fis: 0 };
    c.ciro += sayi(r.net_ciro);
    c.fis += sayi(r.fis_sayisi);
    h[grup] = c;
    map.set(sube, h);
  }
  const satir = (sube: string, hucreler: Record<string, SubeHucre>): SubeSatiri => {
    const m = hucreler['Mağaza'];
    return {
      sube,
      hucreler,
      toplam: Object.values(hucreler).reduce((t, x) => t + x.ciro, 0),
      sepet: m && m.fis > 0 ? m.ciro / m.fis : null,
    };
  };
  const satirlar = Array.from(map.entries())
    .map(([sube, h]) => satir(sube, h))
    .sort((a, b) => b.toplam - a.toplam);
  const genel: Record<string, SubeHucre> = {};
  for (const s of satirlar) {
    for (const [g, x] of Object.entries(s.hucreler)) {
      const c = genel[g] ?? { ciro: 0, fis: 0 };
      c.ciro += x.ciro;
      c.fis += x.fis;
      genel[g] = c;
    }
  }
  return { sutunlar, satirlar, toplam: satir('Toplam', genel) };
}

/** Donemde satis yok, tutar yalniz iade (canlida olculdu: ATAKENT E-Ticaret Agustos −₺306,95, 0 fis). */
function yalnizIade(h: SubeHucre | undefined): boolean {
  return !!h && h.fis === 0 && h.ciro < 0;
}

interface Props {
  /** 990040 (sayfa cozer: ekran once, menu yedegi). */
  kaynak: SaKaynak | undefined;
  donem: Donem;
  /** null = tum subeler. Secildiginde blok TEK sube satiri gosterir. */
  sube?: string | null;
  /** G2: tablo secili kanala suzulur; K6: yalniz ilgili grup sutunu + Toplam, sube adi her satirda. */
  kanal?: string | null;
  /** Verilirse sube adi detay dugmesi olur (C-modal: M3 Sube). */
  onSubeSec?: (sube: string) => void;
}

/**
 * Satir 3 sol — Sube karsilastirmasi (G2b/K4): tek tablo `Şube · Mağaza · E-Ticaret · Kurumsal · Toplam · Sepet`
 * + Toplam satiri. Eski yigin grafik, gosterge ve alt liste KALKTI (dar kutuya uc temsil sigdiriliyordu, spec §1).
 * Sorgu `sube: null, kanal: null`: 040 anahtari paylasilir; suzgecler istemcide.
 */
export function SubeBlok({ kaynak, donem, sube = null, kanal = null, onSubeSec }: Props) {
  const kanalQ = useSaSorgu<KanalSatiri>(kaynak, { donem, sube: null, kanal: null });

  const tablo = useMemo<SubeTablosu>(() => {
    const rows = kanalSuz(subeSuz(kanalQ.data?.data ?? [], sube), kanal);
    const sutunlar =
      kanal !== null
        ? [kanalGrubu(kanal) ?? kanal]
        : kanalSirala(Array.from(new Set([...KANAL_AGACI.map((g) => g.grup), ...rows.map((r) => String(r.kanal_grup))])));
    return subeTablosu(rows, sutunlar);
  }, [kanalQ.data, sube, kanal]);

  const sepetVar = sepetHesaplanirMi(kanal);
  const sutunEtiketi = (grup: string) => kanal ?? grup;

  if (!kaynak) return null; // kaynak yok -> sorgu bile acilmaz
  if (kanalQ.isLoading)
    return <BlokIskeleti testid="blok-sube-iskelet" baslik="Şube karşılaştırması" yukseklik={160} satir={4} />;
  // Bos durum kurali: suzgec YOKKEN 2'den az sube varsa kiyaslanacak bir sey yok. Sube SECILIYSE tek satir beklenir.
  if (sube === null && tablo.satirlar.length < 2) return null;
  if (tablo.satirlar.length === 0) return null;

  const iadeNotlari = tablo.satirlar.flatMap((s) =>
    tablo.sutunlar
      .filter((g) => yalnizIade(s.hucreler[g]))
      .map((g) => `${s.sube} · ${sutunEtiketi(g)}: dönemde satış yok, tutar yalnız iade.`),
  );

  return (
    <div data-testid="blok-sube">
      <SectionCard
        title="Şube karşılaştırması"
        subtitle={kanal !== null ? `${kanal} kanalı · net ciro` : 'Şube × kanal grubu · net ciro'}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-[12px] text-ink-500 border-b border-border">
                <th className="text-left font-medium py-2 pr-3">Şube</th>
                {tablo.sutunlar.map((g) => (
                  <th key={g} data-testid={`sube-sutun-${g}`} className="text-right font-medium py-2 px-3 whitespace-nowrap">
                    <span
                      className="inline-block w-2 h-2 rounded-[2px] mr-1.5 align-middle"
                      aria-hidden="true"
                      style={{ background: kanalRengi(g) }}
                    />
                    {sutunEtiketi(g)}
                  </th>
                ))}
                <th className="text-right font-medium py-2 px-3">Toplam</th>
                {sepetVar && (
                  <th className="text-right font-medium py-2 pl-3" title="Mağaza kanalı sepet ortalaması">Sepet</th>
                )}
              </tr>
            </thead>
            <tbody>
              {tablo.satirlar.map((s) => (
                <tr key={s.sube} data-testid={`sube-satir-${s.sube}`} className="border-b border-border">
                  <td className="py-2 pr-3 font-medium text-ink-900 truncate">
                    {onSubeSec ? (
                      <button
                        type="button"
                        data-testid={`sube-detay-${s.sube}`}
                        onClick={() => onSubeSec(s.sube)}
                        title={`${s.sube} ayrıntısı`}
                        className="focus-ring hover:underline text-left"
                      >
                        {s.sube}
                      </button>
                    ) : (
                      s.sube
                    )}
                  </td>
                  {tablo.sutunlar.map((g) => {
                    const h = s.hucreler[g];
                    return (
                      <td
                        key={g}
                        data-testid={`sube-hucre-${s.sube}-${g}`}
                        className={`py-2 px-3 text-right tnum whitespace-nowrap ${h && h.ciro < 0 ? 'text-neg-700' : 'text-ink-900'}`}
                      >
                        {fmtTRY(h?.ciro ?? 0, { decimals: 0 })}
                        {yalnizIade(h) ? '*' : ''}
                      </td>
                    );
                  })}
                  <td data-testid={`sube-hucre-${s.sube}-Toplam`} className="py-2 px-3 text-right tnum text-ink-900 whitespace-nowrap">
                    {fmtTRY(s.toplam, { decimals: 0 })}
                  </td>
                  {sepetVar && (
                    <td data-testid={`sube-hucre-${s.sube}-Sepet`} className="py-2 pl-3 text-right tnum text-ink-500 whitespace-nowrap">
                      {s.sepet !== null ? fmtTRY(s.sepet) : '—'}
                    </td>
                  )}
                </tr>
              ))}
              <tr data-testid="sube-toplam-satir" className="border-t border-ink-200 font-semibold">
                <td className="py-2 pr-3 text-ink-900">Toplam</td>
                {tablo.sutunlar.map((g) => (
                  <td key={g} data-testid={`sube-toplam-${g}`} className="py-2 px-3 text-right tnum text-ink-900 whitespace-nowrap">
                    {fmtTRY(tablo.toplam.hucreler[g]?.ciro ?? 0, { decimals: 0 })}
                  </td>
                ))}
                <td data-testid="sube-toplam-Toplam" className="py-2 px-3 text-right tnum text-ink-900 whitespace-nowrap">
                  {fmtTRY(tablo.toplam.toplam, { decimals: 0 })}
                </td>
                {sepetVar && (
                  <td data-testid="sube-toplam-Sepet" className="py-2 pl-3 text-right tnum text-ink-900 whitespace-nowrap">
                    {tablo.toplam.sepet !== null ? fmtTRY(tablo.toplam.sepet) : '—'}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
        {iadeNotlari.length > 0 && (
          <p data-testid="sube-iade-notu" className="text-[12px] text-ink-500 mt-2.5">
            * {iadeNotlari.join(' ')}
          </p>
        )}
      </SectionCard>
    </div>
  );
}
